import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors duration-200"
      >
        <div className="w-[180px] h-[120px] shrink-0 rounded-lg overflow-hidden relative">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {article.hot && (
            <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
              热门
            </span>
          )}
        </div>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <h3 className="text-sm font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
              {article.title}
            </h3>
            <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-foreground-400 mt-2">
            <span>{article.publishDate}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.readTime}阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {article.hot && (
            <span className="px-2.5 py-0.5 text-[10px] font-semibold rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
              热门
            </span>
          )}
          {article.exclusive && (
            <span className="px-2.5 py-0.5 text-[10px] font-semibold rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
              独家
            </span>
          )}
        </div>
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] text-foreground-400">{article.publishDate}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
          <span className="text-[11px] text-foreground-400">{article.readTime}阅读</span>
        </div>
        <h3 className="text-sm md:text-base font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
      </div>
    </Link>
  );
}